import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };

  return (
    <div className={`my-4 rounded-xl border-l-4 px-4 py-3 ${styles[type]}`}>
      <span className="font-semibold text-sm">{labels[type]}:</span>{' '}
      <span className="text-sm">{children}</span>
    </div>
  );
}

function HttpMethod({ method, path }: { method: string; path: string }) {
  const methodColors: Record<string, string> = {
    GET: 'bg-success-100 dark:bg-success-500/20 text-success-700 dark:text-success-400',
    POST: 'bg-info-100 dark:bg-info-500/20 text-info-700 dark:text-info-400',
  };

  return (
    <div className="flex items-center gap-3 my-3 px-4 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
      <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${methodColors[method] || ''}`}>
        {method}
      </span>
      <code className="text-sm text-gray-700 dark:text-gray-300 font-mono">{path}</code>
    </div>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'endpoint', label: 'API Endpoint' },
  { id: 'query-parameters', label: 'Query Parameters' },
  { id: 'response-formats', label: 'Response Formats' },
  { id: 'what-it-covers', label: 'What the Guide Covers' },
  { id: 'otlp-in-guide', label: 'OTLP Coverage' },
  { id: 'usage-with-ai', label: 'Using with AI Assistants' },
  { id: 'cursor', label: 'Cursor' },
  { id: 'github-copilot', label: 'GitHub Copilot' },
  { id: 'windsurf', label: 'Windsurf' },
  { id: 'claude', label: 'Claude' },
  { id: 'custom-agents', label: 'Custom AI Agents' },
  { id: 'rate-limits', label: 'Rate Limits' },
];

export function AiLoggingGuidePage() {
  useDocumentMeta({
    title: 'AI Logging Guide: LogPulse Docs',
    description: 'Machine-readable logging integration guide for AI coding assistants. Provides structured instructions for Cursor, Copilot, Windsurf, and Claude to implement LogPulse logging correctly.',
  });

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0 max-w-4xl">
        {/* Page header */}
        <div className="mb-10">
          <div className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 mb-2">
            <span>Integrations</span>
            <span className="text-gray-300 dark:text-gray-600">/</span>
            <span>AI Logging Guide</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            AI Logging Guide
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
            A machine-readable endpoint that provides AI coding assistants with everything they need to implement structured logging for LogPulse, including data schema, code examples, OTLP integration, LPQL queries, and best practices.
          </p>
        </div>

        {/* Overview */}
        <Section id="overview" title="Overview">
          <P>
            The AI Logging Guide is a public API endpoint designed to be consumed by AI coding assistants like Cursor, GitHub Copilot, Windsurf, and Claude. It provides a comprehensive, structured document that teaches AI assistants how to implement LogPulse-compatible structured logging in any application.
          </P>
          <P>
            By pointing your AI assistant at this endpoint, it gains context about the LogPulse data model, ingest endpoints (REST API and OTLP), recommended attribute patterns, and working code examples, enabling it to generate correct logging code on the first try.
          </P>
          <Callout type="tip">
            This endpoint requires no authentication and is publicly available. It is rate-limited to prevent abuse but designed to be fetched by AI tools during coding sessions.
          </Callout>
        </Section>

        {/* Endpoint */}
        <Section id="endpoint" title="API Endpoint">
          <HttpMethod method="GET" path="https://api.logpulse.io/api/v1/ai/logging-guide" />
          <P>
            Returns a comprehensive logging integration guide. No API key required. The response is cached with a 1-hour TTL for performance.
          </P>
        </Section>

        {/* Query parameters */}
        <Section id="query-parameters" title="Query Parameters">
          <Table
            headers={['Parameter', 'Type', 'Default', 'Description']}
            rows={[
              ['language', 'string', 'generic', 'Language for code examples: nodejs, python, go, java, csharp, generic'],
              ['format', 'string', 'markdown', 'Response format: markdown or json'],
            ]}
          />

          <SubSection id="language-options" title="Language Options">
            <Table
              headers={['Value', 'Description', 'Example URL']}
              rows={[
                ['nodejs', 'Node.js / TypeScript: Logger class with batching, Express/Fastify middleware', '?language=nodejs'],
                ['python', 'Python: Logger class with thread-safe batching, FastAPI middleware', '?language=python'],
                ['go', 'Go: Logger with goroutine-safe buffering, functional options pattern', '?language=go'],
                ['java', 'Java 17+: Logger with scheduled flushing, AutoCloseable', '?language=java'],
                ['csharp', 'C# / .NET 8+: Async logger with IAsyncDisposable', '?language=csharp'],
                ['generic', 'curl examples for any language: REST API + OTLP', '?language=generic'],
              ]}
            />
          </SubSection>
        </Section>

        {/* Response formats */}
        <Section id="response-formats" title="Response Formats">
          <SubSection id="markdown-format" title="Markdown (default)">
            <P>
              Returns the guide as a structured Markdown document. This is the preferred format for AI assistants that support Markdown context (most do).
            </P>
            <CodeBlock title="Example request">{`GET https://api.logpulse.io/api/v1/ai/logging-guide?language=nodejs`}</CodeBlock>
            <P>
              Response: <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">Content-Type: text/markdown; charset=utf-8</code>
            </P>
          </SubSection>

          <SubSection id="json-format" title="JSON">
            <P>
              Returns the guide as a structured JSON object with typed fields. Useful for programmatic consumption by custom AI agents or build tools.
            </P>
            <CodeBlock title="Example request">{`GET https://api.logpulse.io/api/v1/ai/logging-guide?language=nodejs&format=json`}</CodeBlock>
            <P>
              Response: <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">Content-Type: application/json; charset=utf-8</code>
            </P>
            <P>
              The JSON response includes structured sections for endpoints, schema, categories, best practices, query examples, OTLP configuration, and code examples URLs.
            </P>
          </SubSection>
        </Section>

        {/* What the guide covers */}
        <Section id="what-it-covers" title="What the Guide Covers">
          <P>
            The guide provides AI assistants with a complete reference for LogPulse integration:
          </P>
          <div className="my-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { title: 'Data Model', desc: 'Full log schema with core fields, Kubernetes fields, and the attributes system' },
              { title: 'Authentication', desc: 'Bearer token authentication for all ingest endpoints' },
              { title: 'REST API', desc: 'POST /api/v1/logs: single and batch log ingestion with examples' },
              { title: 'OTLP Integration', desc: 'OTLP Logs + Traces endpoints, severity mapping, K8s metadata, Collector config, SDK env vars' },
              { title: 'Logging Categories', desc: '7 recommended categories with structured attributes: auth, security, http, database, error, business, system' },
              { title: 'LPQL Queries', desc: 'Example queries for each category so AI assistants can help users search logs' },
              { title: 'Code Examples', desc: 'Production-ready logger classes with batching, retry logic, and convenience methods per language' },
              { title: 'Best Practices', desc: 'Do/don\'t guidelines for structured logging, attribute naming, and security' },
            ].map((item, i) => (
              <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">{item.title}</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* OTLP coverage */}
        <Section id="otlp-in-guide" title="OTLP Coverage in the Guide">
          <P>
            The guide includes a dedicated OTLP section that covers both the logs and traces endpoints. This allows AI assistants to generate OpenTelemetry-compatible configurations and understand how LogPulse processes OTLP data.
          </P>

          <SubSection id="otlp-logs-guide" title="OTLP Logs">
            <P>
              The guide documents the <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">POST /v1/logs</code> endpoint and explains how LogPulse converts OTLP log records:
            </P>
            <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-3">
              <li>Timestamp conversion from nanosecond Unix timestamps to ISO 8601</li>
              <li>Severity number mapping (1–4 = trace, 5–8 = debug, 9–12 = info, 13–16 = warn, 17–20 = error, 21+ = fatal)</li>
              <li>Automatic Kubernetes metadata extraction from resource attributes</li>
              <li>Trace context preservation (traceId, spanId) for log-to-trace correlation</li>
              <li>Full OTLP ExportLogsServiceRequest JSON body example</li>
            </ul>
          </SubSection>

          <SubSection id="otlp-traces-guide" title="OTLP Traces">
            <P>
              The guide documents the <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">POST /v1/traces</code> endpoint and explains how spans are converted to searchable log records:
            </P>
            <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1 mb-3">
              <li>Span field mapping (traceId, spanId, parentSpanId, kind, status, duration)</li>
              <li>HTTP attribute extraction (method, URL, status code)</li>
              <li>Service name and operation name mapping</li>
              <li>LPQL query examples for traces (error spans, slow spans, trace trees)</li>
              <li>Full OTLP ExportTraceServiceRequest JSON body example</li>
            </ul>
          </SubSection>

          <SubSection id="otlp-collector-guide" title="Collector & SDK Configuration">
            <P>
              The guide includes a production-ready OpenTelemetry Collector configuration for Kubernetes that ships both logs and traces to LogPulse, including k8sattributes processor setup and batch processing. It also documents the universal OTLP SDK environment variables that work across all languages:
            </P>
            <CodeBlock title="OTel SDK environment variables">{`OTEL_EXPORTER_OTLP_ENDPOINT=https://api.logpulse.io
OTEL_EXPORTER_OTLP_HEADERS="Authorization=Bearer YOUR_API_KEY"
OTEL_EXPORTER_OTLP_PROTOCOL=http/json`}</CodeBlock>
          </SubSection>
        </Section>

        {/* Usage with AI assistants */}
        <Section id="usage-with-ai" title="Using with AI Assistants">
          <P>
            Each AI assistant has a different mechanism for providing context. Below are setup instructions for the most popular tools.
          </P>

          <SubSection id="cursor" title="Cursor">
            <P>
              Add the guide URL to your project rules so Cursor has context in every conversation:
            </P>
            <CodeBlock title=".cursor/rules/logpulse.mdc">{`---
description: LogPulse structured logging integration guide
globs: ["src/**/*.ts", "src/**/*.tsx", "src/**/*.js"]
alwaysApply: false
---

When implementing logging, fetch the LogPulse guide for reference:
https://api.logpulse.io/api/v1/ai/logging-guide?language=nodejs

Follow the schema, attribute conventions, and best practices described in the guide.
Always use the \`attributes\` field for structured data. All attribute values must be strings.
Use the recommended \`category\` values: authentication, security, http, database, error, business, system.`}</CodeBlock>
          </SubSection>

          <SubSection id="github-copilot" title="GitHub Copilot">
            <P>
              Add a custom instruction file to your repository:
            </P>
            <CodeBlock title=".github/copilot-instructions.md">{`# LogPulse Logging Guide

When implementing logging in this project, refer to the LogPulse AI Logging Guide:
https://api.logpulse.io/api/v1/ai/logging-guide?language=nodejs

Key rules:
- Send logs to POST https://api.logpulse.io/api/v1/logs with Bearer token auth
- Always include \`index\`, \`level\`, \`source\`, and \`event\` fields
- Use \`attributes\` for structured metadata (all values must be strings)
- For OpenTelemetry, use OTLP endpoints: POST /v1/logs and POST /v1/traces`}</CodeBlock>
          </SubSection>

          <SubSection id="windsurf" title="Windsurf">
            <P>
              Add a global rule or project rule in Windsurf with the guide URL:
            </P>
            <CodeBlock title=".windsurfrules">{`When implementing logging for this project, use the LogPulse integration guide:
https://api.logpulse.io/api/v1/ai/logging-guide?language=nodejs

Follow all conventions described in the guide including structured attributes,
log categories, and OTLP integration patterns.`}</CodeBlock>
          </SubSection>

          <SubSection id="claude" title="Claude (Claude Code / claude.ai)">
            <P>
              Add the guide to your project's CLAUDE.md or use it as a prompt:
            </P>
            <CodeBlock title="CLAUDE.md">{`# Logging

This project uses LogPulse for structured logging.

Integration guide: https://api.logpulse.io/api/v1/ai/logging-guide?language=nodejs
JSON format: https://api.logpulse.io/api/v1/ai/logging-guide?language=nodejs&format=json

When implementing logging, follow the LogPulse guide conventions:
- Use the LogPulseLogger class pattern from the guide
- All attributes must be string values
- Include category, source, and index on every log entry
- For OpenTelemetry: configure OTLP exporters to https://api.logpulse.io`}</CodeBlock>
          </SubSection>

          <SubSection id="custom-agents" title="Custom AI Agents">
            <P>
              For custom AI agents and automation tools, use the JSON format for programmatic access:
            </P>
            <CodeBlock title="Fetch in your agent">{`// Fetch the guide as structured JSON
const response = await fetch(
  'https://api.logpulse.io/api/v1/ai/logging-guide?language=nodejs&format=json'
);
const guide = await response.json();

// Use guide.endpoints, guide.schema, guide.categories, guide.otlp, etc.
// to provide context to your AI agent's system prompt`}</CodeBlock>
            <P>
              The JSON format includes typed sections for endpoints (REST + OTLP logs + OTLP traces), schema fields, logging categories with recommended attributes, best practices, LPQL query examples, and OTLP configuration including severity mapping, span field mappings, and SDK environment variables.
            </P>
          </SubSection>
        </Section>

        {/* Rate limits */}
        <Section id="rate-limits" title="Rate Limits">
          <Table
            headers={['Limit', 'Value']}
            rows={[
              ['Requests per minute', '30'],
              ['Cache TTL', '1 hour (Cache-Control: public, max-age=3600)'],
              ['Authentication', 'Not required'],
            ]}
          />
          <Callout type="info">
            The endpoint is rate-limited to 30 requests per minute per IP address. The response includes a <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">Cache-Control</code> header, so AI tools that respect caching will only fetch the guide once per hour.
          </Callout>
        </Section>
      </div>

      {/* Table of contents */}
      <TableOfContents items={tocItems} />
    </div>
  );
}
